import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormComponent } from "@ez-library/components/ez-form/ez-form.component";
import { EzFormInputType, InputConfig } from "@ez-library/types/ez-form.types";
import { InputSelectOption } from "@template-ui/types";
import { DropdownKeys } from "@app/shared/dropdown-configs/common.dropdown-config";
import { RoleService } from "@app/shared/services/roles.service";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import {
    PhmsIntakeSubmissionFormValues,
    PhmsIntakeDisplayError,
    mapPhmsIntakeError,
} from "./phms-manual-intake-submission.models";
import { PhmsIntakeRuntimeService } from "./phms-intake-runtime.service";
import { PhmsInmateCreateRequest, PhmsInmateIntakeOptionDto } from "./create-inmate/phms-create-inmate.models";
import { PhmsCreateInmateService } from "./create-inmate/phms-create-inmate.service";
import { PHMS_SUBJECT_TERMINOLOGY } from "../phms-subject.terminology";
import { PHMS_FACILITY_TERMINOLOGY } from "../phms-facility.terminology";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";

const PHMS_INTAKE_SUBMIT = "phms.intake.submit";

@Component({
    selector: "phms-manual-intake-submission",
    standalone: true,
    imports: [CommonModule, UiLibraryModule],
    templateUrl: "./phms-manual-intake-submission.component.html",
    styleUrl: "./phms-manual-intake-submission.component.scss",
})
export class PhmsManualIntakeSubmissionComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly roles = inject(RoleService);
    private readonly runtime = inject(PhmsIntakeRuntimeService);
    private readonly createInmateService = inject(PhmsCreateInmateService);
    readonly subjectText = createTerminologyUsageSignals(PHMS_SUBJECT_TERMINOLOGY.manualIntake);
    readonly facilityText = createTerminologyUsageSignals(PHMS_FACILITY_TERMINOLOGY.manualIntake);
    readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.manualIntake);

    @ViewChild("intakeForm")
    private intakeForm?: EzFormComponent;

    readonly canSubmit = this.roles.hasAnyPermissionSignal(PHMS_INTAKE_SUBMIT);

    readonly isSubmitting = signal(false);
    readonly programScopeKeysLoaded = signal(false);
    readonly successMessage = signal<string | null>(null);
    readonly errorState = signal<PhmsIntakeDisplayError | null>(null);

    readonly nameLabel = this.runtime.incarceratedPersonDisplayNameLabel;
    readonly identifierLabel = this.runtime.incarceratedPersonIdentifierLabel;
    readonly programScopeKeyLabel = this.runtime.programScopeKeyLabel;
    readonly programScopeKeyOptions = this.runtime.programScopeKeyOptions;

    readonly hasProgramScopeKeyOptions = computed(() => this.programScopeKeyOptions().length > 0);

    /** Facility / offense-category options from the create-inmate intake-options endpoint. */
    readonly facilityOptions = signal<PhmsInmateIntakeOptionDto[]>([]);
    readonly offenseCategoryOptions = signal<PhmsInmateIntakeOptionDto[]>([]);

    readonly formConfig = computed<InputConfig[]>(() => {
        const nameField: InputConfig = {
            name: "incarceratedPersonDisplayName",
            type: EzFormInputType.TEXT,
            label: this.nameLabel(),
            row: 1,
            width: "fill",
            validation: [Validators.required, Validators.maxLength(200)],
            validationMessages: {
                required: `${this.nameLabel()} is required.`,
                maxlength: `${this.nameLabel()} must be 200 characters or fewer.`,
            },
        };

        const identifierField: InputConfig = {
            name: "incarceratedPersonIdentifier",
            type: EzFormInputType.TEXT,
            label: this.identifierLabel(),
            row: 2,
            width: "fill",
            validation: [Validators.required, Validators.maxLength(64)],
            validationMessages: {
                required: `${this.identifierLabel()} is required.`,
                maxlength: `${this.identifierLabel()} must be 64 characters or fewer.`,
            },
        };

        // Full-record fields (story 1438): the create-inmate path provisions demographics + the
        // intake-lifecycle workflow from these, which is what makes the inmate appear on the
        // parole dashboard immediately after import.
        const dateOfBirthField: InputConfig = {
            name: "dateOfBirth",
            type: EzFormInputType.DATE,
            label: "Date of Birth",
            row: 3,
            width: "fill",
            validation: [Validators.required],
            validationMessages: { required: "Date of Birth is required." },
        };

        const facilityField: InputConfig = {
            name: "facilityCode",
            type: EzFormInputType.SELECT,
            label: this.facilityText.label(),
            row: 4,
            width: "fill",
            options: this.facilityOptions().map<InputSelectOption>((option) => ({
                value: option.code,
                viewValue: option.label,
            })),
            validation: [Validators.required],
            validationMessages: { required: this.facilityText.required() },
        };

        const offenseCategoryField: InputConfig = {
            name: "offenseCategoryCode",
            type: EzFormInputType.SELECT,
            label: "Offense Category",
            row: 5,
            width: "fill",
            options: this.offenseCategoryOptions().map<InputSelectOption>((option) => ({
                value: option.code,
                viewValue: option.label,
            })),
            validation: [Validators.required],
            validationMessages: { required: "Offense Category is required." },
        };

        const offenseSummaryField: InputConfig = {
            name: "offenseSummary",
            type: EzFormInputType.TEXT,
            label: "Offense",
            row: 6,
            width: "fill",
            placeholder: "Controlling offense, e.g. Robbery (PC 211)",
            validation: [Validators.required, Validators.maxLength(4000)],
            validationMessages: {
                required: "Offense is required.",
                maxlength: "Offense must be 4000 characters or fewer.",
            },
        };

        const eligibilityDateField: InputConfig = {
            name: "eligibilityDate",
            type: EzFormInputType.DATE,
            label: this.hearingText.eligibilityDate(),
            row: 7,
            width: "fill",
            validation: [Validators.required],
            validationMessages: {
                required: this.hearingText.eligibilityDateRequired(),
            },
        };

        // Optional: an inmate imported with a hearing already on the books lands scheduled on
        // the calendar; left blank they enter the awaiting-scheduling backlog (the usual path).
        const hearingDateField: InputConfig = {
            name: "hearingDate",
            type: EzFormInputType.DATE,
            label: this.hearingText.optionalDate(),
            row: 8,
            width: "fill",
        };

        const programScopeKeyField: InputConfig = {
            name: "programScopeKey",
            type: EzFormInputType.SELECT,
            label: this.programScopeKeyLabel(),
            row: 9,
            width: "fill",
            dropdownKey: DropdownKeys.phmsIntakeProgramScopeKeys,
            validation: [Validators.required],
            validationMessages: {
                required: `${this.programScopeKeyLabel()} is required.`,
            },
        };

        return [
            nameField,
            identifierField,
            dateOfBirthField,
            facilityField,
            offenseCategoryField,
            offenseSummaryField,
            eligibilityDateField,
            hearingDateField,
            programScopeKeyField,
        ];
    });

    formValues: PhmsIntakeSubmissionFormValues = this.createEmptyFormValues();

    readonly pageTitle = "PHMS Manual Intake Submission";
    readonly pageSubtitle = computed(
        () => `${this.subjectText.description()} Server-side authorization remains authoritative.`,
    );
    readonly submitButtonText = "Submit Intake Submission";

    constructor() {
        this.runtime
            .loadProgramScopeKeyOptions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this.programScopeKeysLoaded.set(true),
                error: () => this.programScopeKeysLoaded.set(true),
            });

        this.runtime.loadFieldLabels().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        this.createInmateService
            .getIntakeOptions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (options) => {
                    this.facilityOptions.set(options.facilities);
                    this.offenseCategoryOptions.set(options.offenseCategories);
                },
                // Options failing to load leaves the selects empty; required validation blocks
                // submit and the page-level error box explains any server rejection.
                error: () => undefined,
            });
    }

    submit(): void {
        if (!this.canSubmit()) {
            this.errorState.set({
                title: "Access denied",
                messages: ["You do not have permission to submit intake submissions."],
                issueCodes: [],
            });
            return;
        }

        if (!this.hasProgramScopeKeyOptions()) {
            this.errorState.set({
                title: "No program scopes configured",
                messages: ["At least one program scope must be configured before submitting an intake."],
                issueCodes: [],
            });
            return;
        }

        this.clearMessages();
        if (!this.ensureFormIsValid("Resolve the highlighted intake errors before submitting.")) {
            return;
        }

        // The create-inmate path (story 1438): one call provisions the promoted intake
        // submission, the demographics row, and the intake-lifecycle workflow — the full record
        // the dashboard, case context, and scheduling all key off. An intake-only submission
        // would leave a person-less ghost the dashboard deliberately filters out.
        const request: PhmsInmateCreateRequest = {
            displayName: this.formValues.incarceratedPersonDisplayName,
            inmateNumber: this.formValues.incarceratedPersonIdentifier,
            dateOfBirth: toIsoDate(this.formValues.dateOfBirth),
            facilityCode: this.formValues.facilityCode || null,
            offenseCategoryCode: this.formValues.offenseCategoryCode || null,
            offenseSummary: this.formValues.offenseSummary.trim() || null,
            eligibilityDate: toIsoDate(this.formValues.eligibilityDate),
            hearingDate: toIsoDate(this.formValues.hearingDate),
            programScopeKey: this.formValues.programScopeKey,
        };
        this.isSubmitting.set(true);

        this.createInmateService
            .createInmate(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (created) => {
                    this.isSubmitting.set(false);
                    this.formValues = this.createEmptyFormValues();
                    this.intakeForm?.clearErrors();
                    void this.router.navigate(["/phms/intake-submissions", created.intakeSubmissionId]);
                },
                error: (error) => {
                    this.isSubmitting.set(false);
                    this.errorState.set(mapPhmsIntakeError(error));
                },
            });
    }

    resetForm(): void {
        this.intakeForm?.clearErrors();
        this.errorState.set(null);
        this.successMessage.set(null);
        this.formValues = this.createEmptyFormValues();
    }

    private ensureFormIsValid(fallbackMessage: string): boolean {
        const formGroup = this.intakeForm?.theFormGroup;
        if (!formGroup || formGroup.valid) {
            return true;
        }

        formGroup.markAllAsTouched();
        const controlErrors = this.intakeForm?.controlErrors?.map((entry) => entry.message) ?? [];
        this.errorState.set({
            title: "Validation failed",
            messages: controlErrors.length > 0 ? [...new Set(controlErrors)] : [fallbackMessage],
            issueCodes: [],
        });

        return false;
    }

    private clearMessages(): void {
        this.successMessage.set(null);
        this.errorState.set(null);
    }

    private createEmptyFormValues(): PhmsIntakeSubmissionFormValues {
        return {
            incarceratedPersonDisplayName: "",
            incarceratedPersonIdentifier: "",
            programScopeKey: "",
            dateOfBirth: null,
            facilityCode: "",
            offenseCategoryCode: "",
            offenseSummary: "",
            eligibilityDate: null,
            hearingDate: null,
        };
    }
}

/** DATE inputs yield a Date or string; the API takes "yyyy-MM-dd" (local date, no time shift). */
function toIsoDate(value: string | Date | null): string | null {
    if (!value) {
        return null;
    }
    if (typeof value === "string") {
        return value.length > 10 ? value.slice(0, 10) : value;
    }
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${value.getFullYear()}-${month}-${day}`;
}
